import { useState, useEffect, useRef, useCallback } from "react";
import { get, isString } from "lodash-es";
import { getList } from "@api";
import { parseQueryString } from "@/utils/toolt";
import { QueryParams } from "@/facilities/model/user";
import useMessageRedux from "@/store/message-redux";

function usePolling({
  url,
  dataKey = "data.body",
  interval = 5000,
  excludeParams = [],
  updateKey,
}: {
  url: string;
  dataKey?: string;
  updateKey?: QueryParams;
  interval?: number;
  excludeParams?: string[];
}) {
  const [data, setData] = useState<any>();
  const [loading, setLoading] = useState(true);
  const intervalTimer = useRef<any>(null);
  const [retryCount, setRetryCount] = useState(0);
  const [params, setParams] = useState();
  const [originParams, setOriginParams] = useState();
  const { message } = useMessageRedux();
  const MAX_RETRIES = 10;

  const getParams = useCallback(() => {
    const urlParms = parseQueryString(updateKey);
    delete urlParms.date;
    setOriginParams({ ...urlParms });
    excludeParams.forEach((key) => {
      delete urlParms[key];
    });
    setParams(urlParms);
    return urlParms;
  }, [updateKey, excludeParams]);

  const fetchData = useCallback(
    async (query?: any, isAddParms = true) => {
      const params = { ...getParams(), ...query };
      try {
        const response = await getList({
          url,
          params: isAddParms ? params : query,
        });
        const body = get(response, dataKey, []);
        setData(body);
        setLoading(false);
        setRetryCount(0);
      } catch (error: any) {
        message.error(isString(error) ? error : error?.detail || error.message);
        setLoading(false);
        setRetryCount((prev) => prev + 1);
      }
    },
    [dataKey, url, message]
  );
  useEffect(() => {
    fetchData();
    intervalTimer.current = setInterval(fetchData, interval);
    return () => clearInterval(intervalTimer.current);
  }, [fetchData, interval]);

  useEffect(() => {
    if (retryCount >= MAX_RETRIES) {
      stopPolling();
    }
  }, [retryCount]);

  const stopPolling = () => {
    if (intervalTimer.current) {
      clearInterval(intervalTimer.current);
      intervalTimer.current = null;
    }
  };

  const startPolling = () => {
    if (!intervalTimer.current) {
      intervalTimer.current = setInterval(fetchData, interval);
    }
  };

  return {
    data,
    loading,
    stopPolling,
    fetchData,
    params,
    originParams,
    startPolling,
  };
}

export default usePolling;
